// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useMemo } from 'react'
import {
  ColumnDef,
  Table,
  Typography,
  SortingState,
  StateMessage,
} from '@teamnine/pcj-ui-v2'
import { List } from '@/utils/material-icons'
import { useNavigate } from 'react-router-dom'

import { Customer as ICustomer } from '@/services/customers'
import { Tenant as ITenant } from '@/services/practices'
import LinkButton from '@/app/shared/components/LinkButton'

const PAGE_SIZE = 30

type ActivePracticesViewProps = {
  practices: ITenant[]
  currentCustomer?: ICustomer
  isLoading: boolean
  searchString: string
  practiceSortState: SortingState
  practicesPageIndex: number
  onSortingChange: (next: SortingState) => void
  onPageChange: (pageIndex: number) => void
  onSearch: (val: string[]) => void
}

export default function ActivePracticesView({
  practices,
  currentCustomer,
  isLoading,
  searchString,
  practiceSortState,
  practicesPageIndex,
  onSortingChange,
  onPageChange,
  onSearch,
}: ActivePracticesViewProps) {
  const navigate = useNavigate()

  const filteredPractices = useMemo(() => {
    const filtered = practices.filter((practice) => {
      if (!searchString) return true
      const search = searchString.trim().toLowerCase()
      return (
        practice.name.toLowerCase().includes(search) ||
        practice.subdomain.toLowerCase().includes(search) ||
        practice.auth0OrgId.toLowerCase().includes(search) ||
        practice.enterprisePracticeId.toLowerCase().startsWith(search) ||
        practice.siteId.toLowerCase().startsWith(search)
      )
    })

    if (!practiceSortState.length) return filtered

    const { id, desc } = practiceSortState[0]
    return [...filtered].sort((a, b) => {
      let result = 0
      if (id === 'practiceName') {
        result = a.name.localeCompare(b.name, undefined, {
          sensitivity: 'base',
        })
      } else if (id === 'subdomain') {
        result = a.subdomain.localeCompare(b.subdomain, undefined, {
          sensitivity: 'base',
        })
      } else if (id === 'auth0OrgId') {
        result = a.auth0OrgId.localeCompare(b.auth0OrgId, undefined, {
          sensitivity: 'base',
        })
      } else if (id === 'enterprisePracticeId') {
        result = a.enterprisePracticeId.localeCompare(
          b.enterprisePracticeId,
          undefined,
          {
            numeric: true,
            sensitivity: 'base',
          }
        )
      } else if (id === 'siteId') {
        result = a.siteId.localeCompare(b.siteId, undefined, {
          sensitivity: 'base',
        })
      }
      return desc ? -result : result
    })
  }, [practices, practiceSortState, searchString])

  const pagedPractices = useMemo(() => {
    const start = practicesPageIndex * PAGE_SIZE
    return filteredPractices.slice(start, start + PAGE_SIZE)
  }, [filteredPractices, practicesPageIndex])

  const practiceColumns: ColumnDef<ITenant>[] = useMemo(
    () => [
      {
        id: 'practiceName',
        header: 'Name',
        accessorKey: 'name' as const,
        enableSorting: true,
        cell: ({ row }) => (
          <LinkButton
            label={row.original.name}
            onClick={() =>
              navigate(
                `/customer-management/${currentCustomer?.id}/practices/${row.original.id}`
              )
            }
          />
        ),
      },
      {
        id: 'enterprisePracticeId',
        header: 'Practice ID',
        accessorKey: 'enterprisePracticeId' as const,
        enableSorting: true,
        cell: ({ row }) => (
          <Typography>{row.original.enterprisePracticeId || 'N/A'}</Typography>
        ),
      },
      {
        id: 'siteId',
        header: 'Site ID',
        accessorKey: 'siteId' as const,
        enableSorting: true,
        cell: ({ row }) => (
          <Typography>{row.original.siteId || 'N/A'}</Typography>
        ),
      },
      {
        id: 'subdomain',
        header: 'Subdomain',
        accessorKey: 'subdomain' as const,
        enableSorting: true,
      },
      {
        id: 'auth0OrgId',
        header: 'Auth0 Org ID',
        accessorKey: 'auth0OrgId' as const,
        enableSorting: true,
      },
    ],
    [navigate, currentCustomer]
  )

  if (!isLoading && practices.length === 0) {
    return <StateMessage title="There are no active practices." Icon={List} />
  }

  return (
    <Table<ITenant>
      borderless
      data={pagedPractices}
      totalCount={filteredPractices.length}
      isLoading={isLoading}
      columns={practiceColumns}
      sorting={{
        state: practiceSortState,
        onSortingChange: (next) => {
          onSortingChange(next)
          onPageChange(0)
        },
      }}
      search={{
        type: 'single',
        onSearch,
        placeholder: 'Enter practice name',
        label: 'Search practice',
        width: 400,
      }}
      pagination={{
        mode: 'paged',
        pageIndex: practicesPageIndex,
        pageSize: PAGE_SIZE,
        totalCount: filteredPractices.length,
        onPageChange: ({ pageIndex }) => {
          onPageChange(pageIndex)
        },
      }}
      empty={{
        title: 'There are no active practices.',
        Icon: List,
      }}
    />
  )
}
